/**
 * Single derivation feeding the Bank Transactions page's monthly cash-flow
 * chart (region split), its Reconciled/Unreconciled figures row, and its
 * Inflow/Outflow stat cards, so the cards, chart and row read from the
 * exact same computation and can never disagree (a dedicated test asserts
 * this in lib/__tests__/transactionsCashflow.test.js).
 *
 * Input `rows` is the raw GET /transactions/monthly-cashflow response shape:
 * [{ month, region: 'UK'|'France'|'Unassigned', is_reconciled, currency,
 *    inflow, outflow }], native-currency amounts, never mixed across
 * currency until converted here. `months` is the fixed set of 'YYYY-MM'
 * month keys to render (the caller passes a full Jan..Dec spine, e.g.
 * generateMonthAxis('ytd') -- CLAUDE.md Prompt: Bank Transactions cash-flow
 * chart is never windowed to months-with-data). A row whose month falls
 * outside that spine still contributes to `totals` (unchanged) but is not
 * placed in any per-month series, mirroring the Revenue/Profit/Margin
 * chart's own fixed-year spine.
 *
 * Chart series (`byRegion`, still exposed as `byReconciled` for the same
 * per-month rows) render inflow and outflow as two same-direction (both
 * positive) bars, side by side per month -- direction is expressed by which
 * series group (`_in` vs `_out`) a value belongs to, never by sign
 * (StackedFlowChart.jsx). `totals` keeps outflow signed internally (native
 * accumulation), flipped to a magnitude once at the very end for stat-card
 * display -- unchanged by this.
 *
 * `figuresRow` is the per-month Reconciled/Unreconciled net (in - out
 * magnitude) figures row rendered under the chart. Because it is derived
 * from the identical `Reconciled_*`/`Unreconciled_*` accumulators as
 * `byRegion`'s `UK_*`/`FR_*`/`Unassigned_*` fields (both sides of every row
 * update in the same pass below), `reconciled + unreconciled` always equals
 * `regionTotal` for a given month by construction -- there is no second,
 * divergent basis to unify (see transactionsCashflowFiguresRow.test.js). A
 * month with zero underlying rows (not just zero net value) gets `null` in
 * all three fields so the figures row can render "-" rather than a
 * misleading "0".
 *
 * A currency with no configured rate in `fxRatesSpot` (e.g. a Wise activity
 * booked onto an account whose own currency differs from the activity's --
 * bank-balances/summary's seen_currencies is derived from bank_accounts,
 * not transactions, so that currency's rate is never even fetched) is never
 * silently treated as 1:1 with the display currency: it is excluded from
 * every converted total/series and its native amount surfaced via the
 * returned `unconverted` list instead (CLAUDE.md section 3 -- a missing
 * rate never falls back to 1.0).
 */
import { getConversionRateStrict } from './currencyConvert';

const REGION_KEY = { UK: 'UK', France: 'FR', Unassigned: 'Unassigned' };

function emptyMonthRow(month) {
  return {
    month,
    UK_in: 0, FR_in: 0, Unassigned_in: 0,
    UK_out: 0, FR_out: 0, Unassigned_out: 0,
    Reconciled_in: 0, Unreconciled_in: 0,
    Reconciled_out: 0, Unreconciled_out: 0,
    // Disjoint per-doc counts (a transaction is inflow XOR outflow, never
    // both), parallel to the amount fields above -- feeds the figures
    // row's Reconciled/Unreconciled COUNTS line.
    Reconciled_in_count: 0, Unreconciled_in_count: 0,
    Reconciled_out_count: 0, Unreconciled_out_count: 0,
  };
}

function emptyTotalsSide() {
  return { total: 0, UK: 0, FR: 0, Unassigned: 0, Reconciled: 0, Unreconciled: 0 };
}

/**
 * @param {Array} rows - raw monthly-cashflow rows (native currency, per month/region/reconciled/currency)
 * @param {Array<string>} months - ordered 'YYYY-MM' months to include (from the API response)
 * @param {object} opts
 * @param {string} opts.displayCurrency
 * @param {object} opts.fxRatesSpot
 */
export function buildCashflowSummary(rows, months, { displayCurrency, fxRatesSpot }) {
  const monthMap = {};
  months.forEach((m) => { monthMap[m] = emptyMonthRow(m); });

  const totals = { inflow: emptyTotalsSide(), outflow: emptyTotalsSide() };
  const unconvertedByCurrency = {};
  // Every row that reaches this derivation at all counts as "this month had
  // data", independent of whether it later converts cleanly -- matches the
  // backend's own months_seen (routes/transactions.py), so figuresRow can
  // tell "genuinely no transactions" (render "-") apart from "transactions
  // existed but netted to zero" (render the real zero).
  const monthsWithData = new Set();

  (rows || []).forEach((row) => {
    if (row.month) monthsWithData.add(row.month);
    if (row.inflow == null || row.outflow == null) return;

    const rate = getConversionRateStrict(row.currency, displayCurrency, fxRatesSpot);
    if (rate == null) {
      // No real rate known for this currency -- never naked-sum it into the
      // converted total at an assumed 1:1. Surface the native amount
      // instead so the operator can see exactly what was left out.
      const bucket = unconvertedByCurrency[row.currency] || { currency: row.currency, inflow: 0, outflow: 0 };
      bucket.inflow += row.inflow;
      bucket.outflow += row.outflow;
      unconvertedByCurrency[row.currency] = bucket;
      return;
    }

    const cIn = row.inflow * rate;
    const cOut = row.outflow * rate;

    const regionKey = REGION_KEY[row.region] || 'Unassigned';
    const reconKey = row.is_reconciled ? 'Reconciled' : 'Unreconciled';
    // Chart series are same-direction: outflow renders as a positive
    // magnitude bar next to (not below/mirroring) the inflow bar.
    const cOutMagnitude = Math.abs(cOut);

    if (!monthMap[row.month]) monthMap[row.month] = emptyMonthRow(row.month);
    const monthRow = monthMap[row.month];
    monthRow[`${regionKey}_in`] += cIn;
    monthRow[`${regionKey}_out`] += cOutMagnitude;
    monthRow[`${reconKey}_in`] += cIn;
    monthRow[`${reconKey}_out`] += cOutMagnitude;
    monthRow[`${reconKey}_in_count`] += row.inflow_count || 0;
    monthRow[`${reconKey}_out_count`] += row.outflow_count || 0;

    totals.inflow.total += cIn;
    totals.inflow[regionKey] += cIn;
    totals.inflow[reconKey] += cIn;
    // outflow accumulates signed (negative) here, matching byRegion/byReconciled;
    // flipped to a magnitude below, once, only for stat-card display.
    totals.outflow.total += cOut;
    totals.outflow[regionKey] += cOut;
    totals.outflow[reconKey] += cOut;
  });

  const orderedMonths = months.length ? months : Object.keys(monthMap).sort();
  const byRegion = orderedMonths.map((m) => monthMap[m] || emptyMonthRow(m));
  const byReconciled = byRegion; // same per-month rows carry both splits' keys

  // Reconciled/Unreconciled figures row shown under the chart -- net (in -
  // out magnitude) per month, same basis as the region chart's bars, so
  // reconciled + unreconciled always sums to that month's region total.
  const figuresRow = byRegion.map((row) => {
    const hasData = monthsWithData.has(row.month);
    const reconciled = row.Reconciled_in - row.Reconciled_out;
    const unreconciled = row.Unreconciled_in - row.Unreconciled_out;
    const regionTotal = (row.UK_in + row.FR_in + row.Unassigned_in)
      - (row.UK_out + row.FR_out + row.Unassigned_out);
    // Counts: total transactions behind each net figure above (inflow +
    // outflow docs, not their netted difference) -- same
    // Reconciled_*_count/Unreconciled_*_count accumulators as the amounts,
    // so reconciledCount + unreconciledCount always sums to
    // regionTotalCount for a given month, same parity guarantee as the
    // amounts (transactionsCashflowFiguresRow.test.js).
    const reconciledCount = row.Reconciled_in_count + row.Reconciled_out_count;
    const unreconciledCount = row.Unreconciled_in_count + row.Unreconciled_out_count;
    const regionTotalCount = reconciledCount + unreconciledCount;
    return {
      month: row.month,
      reconciled: hasData ? reconciled : null,
      unreconciled: hasData ? unreconciled : null,
      regionTotal: hasData ? regionTotal : null,
      reconciledCount: hasData ? reconciledCount : null,
      unreconciledCount: hasData ? unreconciledCount : null,
      regionTotalCount: hasData ? regionTotalCount : null,
    };
  });

  const outflowDisplay = {
    total: Math.abs(totals.outflow.total),
    UK: Math.abs(totals.outflow.UK),
    FR: Math.abs(totals.outflow.FR),
    Unassigned: Math.abs(totals.outflow.Unassigned),
    Reconciled: Math.abs(totals.outflow.Reconciled),
    Unreconciled: Math.abs(totals.outflow.Unreconciled),
  };

  return {
    byRegion,
    byReconciled,
    figuresRow,
    totals: { inflow: totals.inflow, outflow: outflowDisplay },
    // Native-currency amounts excluded from every total/series above
    // because no real FX rate was available -- [] when everything
    // converted cleanly, never omitted from the return shape.
    unconverted: Object.values(unconvertedByCurrency),
  };
}
